/* Southern Atelier – product page: variant selection, size sheet, gallery thumbs, add to bag */
import { saAddToCart } from './sa-cart.js';

class SaProduct {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-sa-form]');
    this.idInput = root.querySelector('[data-sa-variant-id]');
    this.addBtn = root.querySelector('[data-sa-add]');
    this.error = root.querySelector('[data-sa-error]');
    try { this.variants = JSON.parse(root.querySelector('[data-sa-variants]')?.textContent || '[]'); } catch (e) { this.variants = []; }
    this.fieldsets = [...root.querySelectorAll('[data-sa-option]')];
    this.sizeFs = this.fieldsets.find((f) => f.dataset.kind === 'size');
    this.needsSize = !!this.sizeFs && !this.sizeFs.querySelector('input:checked');
    this.bindOptions();
    this.bindSize();
    this.bindGallery();
    this.bindDrawers();
    this.bindSubmit();
    this.refresh(false);
    this.filterByColor(true);
  }

  selected() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
  }

  match(values) {
    return this.variants.find((v) => v.options.every((o, i) => values[i] == null || o === values[i]));
  }

  bindOptions() {
    this.fieldsets.forEach((fs) => fs.addEventListener('change', () => {
      if (fs === this.sizeFs) this.needsSize = false;
      this.refresh(true);
      if (fs.dataset.kind === 'swatch') this.filterByColor(false);
    }));
  }

  /* All images stay visible; selecting a colour jumps to that colour's first image */
  filterByColor(initial) {
    const gallery = this.root.querySelector('[data-sa-gallery]');
    const idx = Number(gallery?.dataset.colorIndex ?? -1);
    if (!gallery || idx < 0 || initial) return;
    const colour = this.selected()[idx];
    const slides = [...this.root.querySelectorAll('.sa-pdp__slide')];
    const i = slides.findIndex((s) => s.dataset.color === colour);
    if (i > -1) gallery.goTo?.(i);
  }

  refresh(fromUser) {
    const values = this.selected();
    const full = values.every((v) => v != null);
    const variant = full ? this.match(values) : null;

    // availability per value (given the other selected options)
    this.fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const test = values.slice();
        test[i] = input.value;
        const v = this.variants.find((x) => x.options.every((o, j) => test[j] == null || o === test[j]));
        const available = !!v && v.available;
        const label = input.closest('label');
        label?.classList.toggle('is-unavailable', !available);
        if (fs.dataset.kind === 'size') {
          const meta = label.querySelector('[data-sa-size-meta]');
          if (meta) meta.textContent = !v || !v.available ? this.addBtn.dataset.labelSoldout : (v.qty != null && v.qty > 0 && v.qty <= 3 ? `${v.qty} left` : '');
        }
      });
      const out = fs.querySelector('[data-sa-option-value]');
      if (out) out.textContent = values[i] ?? '';
    });

    this.root.classList.toggle('is-size-chosen', !this.needsSize);
    const sizeLabel = this.root.querySelector('[data-sa-size-label]');
    if (sizeLabel) sizeLabel.textContent = this.needsSize ? this.addBtn.dataset.labelChoose : (values[this.fieldsets.indexOf(this.sizeFs)] ?? this.addBtn.dataset.labelChoose);

    if (variant) {
      this.idInput.value = variant.id;
      const price = this.root.querySelector('[data-sa-price]');
      if (price) price.innerHTML = variant.compare
        ? `<s class="sa-price__compare">${variant.compare}</s> <span class="sa-price__sale">${variant.price}</span>`
        : `<span class="sa-price__regular">${variant.price}</span>`;
      if (fromUser) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        if (variant.media) this.showMedia(variant.media);
      }
    }

    // button state
    const b = this.addBtn;
    if (this.needsSize) { b.disabled = false; b.textContent = b.dataset.labelChoose; b.dataset.mode = 'choose'; }
    else if (!variant || !variant.available) { b.disabled = true; b.textContent = b.dataset.labelSoldout; b.dataset.mode = 'soldout'; }
    else { b.disabled = false; b.textContent = b.dataset.labelAdd; b.dataset.mode = 'add'; }
  }

  bindSize() {
    if (!this.sizeFs) return;
    const row = this.sizeFs.querySelector('[data-sa-size-row]');
    const hint = this.sizeFs.querySelector('[data-sa-size-hint]');
    this.openSize = () => {
      row?.classList.add('is-required');
      if (hint) hint.hidden = false;
      this.sizeFs.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.sizeFs.querySelector('input')?.focus({ preventScroll: true });
    };
    this.sizeFs.addEventListener('change', () => {
      row?.classList.remove('is-required');
      if (hint) hint.hidden = true;
    });
  }

  bindGallery() {
    const main = this.root.querySelector('[data-sa-gallery]');
    const thumbs = [...this.root.querySelectorAll('[data-sa-thumb]')];
    if (!main) return;
    thumbs.forEach((t) => t.addEventListener('click', () => {
      main.goTo?.(Number(t.dataset.saThumb));
      thumbs.forEach((x) => x.classList.toggle('is-active', x === t));
    }));
    main.addEventListener('sa:slide', () => {
      const track = main.querySelector('[data-sa-track]');
      const visible = [...this.root.querySelectorAll('.sa-pdp__slide')].filter((s) => !s.hidden);
      const i = Math.round(track.scrollLeft / (visible[0]?.offsetWidth || 1));
      const cur = visible[i];
      const all = [...this.root.querySelectorAll('.sa-pdp__slide')];
      thumbs.forEach((t) => t.classList.toggle('is-active', all[Number(t.dataset.saThumb)] === cur));
    });
  }

  showMedia(mediaId) {
    const main = this.root.querySelector('[data-sa-gallery]');
    const slides = [...this.root.querySelectorAll('.sa-pdp__slide')];
    const i = slides.findIndex((s) => s.dataset.mediaId == mediaId);
    if (i > -1) main?.goTo?.(i);
  }

  bindDrawers() {
    this.root.querySelectorAll('[data-sa-drawer-open]').forEach((btn) => btn.addEventListener('click', () => {
      const d = this.root.querySelector(`[data-sa-drawer="${btn.dataset.saDrawerOpen}"]`);
      if (!d) return;
      d.hidden = false; document.documentElement.classList.add('sa-lock');
      d.querySelector('[data-sa-drawer-close]')?.focus();
      const close = () => { d.hidden = true; document.documentElement.classList.remove('sa-lock'); btn.focus(); };
      d.querySelectorAll('[data-sa-drawer-close]').forEach((c) => (c.onclick = close));
      d.onkeydown = (e) => { if (e.key === 'Escape') close(); };
    }));
  }

  bindSubmit() {
    this.form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const b = this.addBtn;
      if (b.dataset.mode === 'choose') { this.openSize?.(); return; }
      if (b.disabled) return;
      this.error.hidden = true;
      b.setAttribute('aria-busy', 'true');
      try {
        await saAddToCart({ id: this.idInput.value, quantity: 1, source: this.form });
        b.textContent = b.dataset.labelAdded;
        setTimeout(() => this.refresh(false), 1800);
      } catch (err) {
        this.error.textContent = err.message;
        this.error.hidden = false;
      } finally {
        b.removeAttribute('aria-busy');
      }
    });
  }
}

const boot = () => document.querySelectorAll('[data-sa-product]').forEach((el) => { if (!el.__sa) el.__sa = new SaProduct(el); });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', boot);
